"use client";

import Link from "next/link";
import Image from "next/image";
import type { Page, WorkPageContent, HomePageContent, DbBlueskyPost } from "./types/cms";
import { HlsPlayer } from "./components/HlsPlayer";
import { CursorLabel } from "./components/CursorLabel";
import { BoardToast } from "./components/board-toast";
import { useCursorLabel } from "./hooks/useCursorLabel";
import { stripHtml } from "../lib/strip-html";
import { getWorkThumbnail } from "../lib/work-thumbnail";

interface HomeClientProps {
  pageContent: HomePageContent;
  featuredWork: Page[];
  featuredWip: DbBlueskyPost[];
}

export function HomeClient({ pageContent, featuredWork, featuredWip }: HomeClientProps) {
  const { isVisible, text, showLabel, hideLabel } = useCursorLabel();

  return (
    <>
      <CursorLabel text={text} isVisible={isVisible} />
      <BoardToast />
      <div className="min-h-screen text-foreground">
        <main className="mt-[100px] px-[20px] pb-[100px] sm:mt-[50px] sm:ml-[200px] sm:px-0">
        <div className="w-full max-w-3xl sm:pl-[50px] sm:pr-4">
          {/* Hero Section */}
          <section className="mb-16 text-[14px] leading-[1.6]">
            <h1 className="sr-only text-2xl">Kasper Marx Andersen - Product Designer</h1>
            <div className="mb-30">
              <div className="opacity-100">
                <span className="rich-text-content" dangerouslySetInnerHTML={{ __html: pageContent.hero_text }} />
              </div>
            </div>
            
            {/* Featured Content */}
            <div className="space-y-30">
              {/* Featured Work */}
              {pageContent.show_featured_work && featuredWork.length > 0 && (
                <div>
                  <h2 className="sr-only">Featured Work</h2>
                  <div className="space-y-4">
                    <p className="text-[14px] font-medium opacity-100">Recent cases:</p>
                    <div className="space-y-10">
                      {featuredWork.map((work) => {
                        const content = work.content as WorkPageContent;
                        const slug = work.slug.replace('work/', '');
                        const thumbnail = getWorkThumbnail(content);
                        
                        return (
                          <article key={work.id} className="group mb-20 last:mb-0">
                            <Link 
                              href={`/work/${slug}`}
                              className="block hover-invert"
                              onMouseEnter={() => showLabel("see case")}
                              onMouseLeave={hideLabel}
                            >
                              {/* Image */}
                              {thumbnail && (
                                <div className="mb-3">
                                  <div className="relative w-full max-w-full overflow-hidden bg-muted" style={{ maxHeight: "700px" }}>
                                    <Image
                                      src={thumbnail.src}
                                      alt={thumbnail.alt || content.title}
                                      width={2000}
                                      height={2000}
                                      className="w-full h-auto object-contain max-w-full hover:opacity-90 transition-opacity"
                                      priority
                                      fetchPriority="high"
                                      style={{ maxWidth: "100%" }}
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {/* Title */}
                              <div className="flex justify-start mb-1">
                                <div className="text-[14px] opacity-100">
                                  {content.title}
                                </div>
                              </div>
                              
                              {/* Description */}
                              {content.overview_text && (
                                <p className="text-[14px] leading-[1.6] opacity-100">
                                  {stripHtml(content.overview_text)}
                                </p>
                              )}
                            </Link>
                          </article>
                        );
                      })}
                    </div>
                    <div className="mt-6">
                      <Link 
                        href="/work" 
                        className="hover-invert inline-block opacity-70 hover:opacity-100"
                      >
                        View all work →
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Featured Work in Progress */}
              {pageContent.show_featured_wip && featuredWip.length > 0 && (
                <div>
                  <h2 className="sr-only">Featured Work in Progress</h2>
                  <div className="space-y-4">
                    <p className="text-[14px] font-medium opacity-100">Work in progress:</p>
                    <div className="space-y-10">
                      {featuredWip.map((post) => {
                        const images = post.embed_data?.images ?? [];
                        const media = post.embed_data?.media ?? [];
                        const firstMedia = images[0] || media[0];
                        const formattedDate = new Date(post.indexed_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric"
                        });
                        
                        // Clean text by removing hashtags
                        const cleanText = post.content.text.replace(/\s#\w+/g, "").replace(/^#\w+\s*/g, "").replace(/\s+/g, " ").trim();
                        
                        return (
                          <article key={post.id} className="group mb-20 last:mb-0">
                            <Link 
                              href={`/feed/${post.slug}`}
                              className="block hover-invert"
                              onMouseEnter={() => showLabel("see post")}
                              onMouseLeave={hideLabel}
                            >
                              {/* Media */}
                              {firstMedia && (
                                <div className="mb-3">
                                  {media[0]?.type === "video" ? (
                                    <div 
                                      className="relative overflow-hidden media-container"
                                      style={{ 
                                        aspectRatio: media[0].aspectRatio?.width && media[0].aspectRatio?.height
                                          ? `${media[0].aspectRatio.width / media[0].aspectRatio.height}`
                                          : "16/9",
                                        maxHeight: "700px",
                                        width: media[0].aspectRatio?.width && media[0].aspectRatio?.height
                                          ? `${Math.min(700 * (media[0].aspectRatio.width / media[0].aspectRatio.height), 700)}px`
                                          : "700px",
                                        maxWidth: "100%"
                                      }}
                                    >
                                      <HlsPlayer
                                        src={media[0].fullsize}
                                        poster={media[0].poster}
                                        className="absolute inset-0 w-full h-full object-contain hls-player"
                                      />
                                    </div>
                                  ) : (
                                    <div className="relative w-full max-w-full overflow-hidden bg-muted" style={{ maxHeight: "700px" }}>
                                      <Image
                                        src={firstMedia.fullsize}
                                        alt={firstMedia.alt || "Work in progress"}
                                        width={2000}
                                        height={2000}
                                        className="w-full h-auto object-contain max-w-full hover:opacity-90 transition-opacity"
                                        priority
                                        style={{ maxWidth: "100%" }}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Date */}
                              <div className="flex justify-start mb-1">
                                <div className="text-[14px] opacity-100">
                                  {formattedDate}
                                  {post.is_featured && (
                                    <span className="ml-2 text-orange-500">★</span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Description */}
                                <p className="text-[14px] leading-[1.6] opacity-100">
                                {cleanText}
                              </p>
                            </Link>
                          </article>
                        );
                      })}
                    </div>
                    <div className="mt-6">
                      <Link 
                        href="/work-in-progress" 
                        className="hover-invert inline-block opacity-70 hover:opacity-100"
                      >
                        View all work in progress →
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Navigation Links */}
          {pageContent.show_navigation_links && (
            <nav className="mb-16" aria-label="Quick navigation">
              <div className="flex flex-wrap gap-6 text-[14px]">
                <Link href="/work-in-progress" className="hover-invert">Work in Progress</Link>
                <Link href="/work" className="hover-invert">Cases</Link>
                <Link href="/info" className="hover-invert">Info</Link>
                <Link href="/links" className="hover-invert">Links</Link>
              </div>
            </nav>
          )}
        </div>
      </main>
    </div>
    </>
  );
}
